


















import { computed, defineComponent, ref, toRef, watch } from "@vue/composition-api";
import { Follower, FollowingsSearchResult } from "@scrile/api-provider/dist/api/FollowingsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { resolveUserIdByRoute } from "@/lib/userHelpers";
import config from "@/config";
import FollowersView from "./components/FollowersView";
import { FollowersMap } from "./types";

export default defineComponent({
  name: "PageFollowers",
  components: { FollowersView },
  props: {
    userId: { type: String, required: true },
    isFollowingPage: { type: Boolean, required: true },
  },
  setup(props, { root }) {
    const { authUser, isAdmin } = useAuthUser();
    const loading = ref(false);
    const scroll = ref(false);
    const user = ref<UserPublicData | null>(null);
    const followingsSearchResult = ref<FollowingsSearchResult | null>(null);
    const authorFollowings = ref<FollowersMap>({});

    const authUserId = computed(() => authUser.value?.id ?? "");
    const isAuthor = computed(() => authUserId.value === props.userId);
    const canGetFollowings = computed(() => config.featurePublicFollowing || isAuthor.value || isAdmin.value);
    const canGetFollowers = computed(() => config.featurePublicFollowers || isAuthor.value || isAdmin.value);

    const getFollowers = async (isFollowingPage: boolean, page: number = 0, replace: boolean = true) => {
      if (!canGetFollowers.value && !canGetFollowings.value) return;
      scroll.value = false;
      try {
        const inputData = isFollowingPage ? { followerId: props.userId } : { userId: props.userId };
        const response = await providers.FollowingsProvider.find({
          data: { ...inputData, sort: "TIME,DESC", page, size: 20 },
        });
        followingsSearchResult.value = {
          ...response,
          result:
            replace || !followingsSearchResult.value
              ? response.result
              : followingsSearchResult.value.result.concat(response.result),
        };

        if (authUser.value && response.result.length !== 0) {
          const reduceToMapCallback = (acc: FollowersMap, i: Follower) => {
            acc[i.userId] = i;
            return acc;
          };
          if (isAuthor.value && isFollowingPage) {
            authorFollowings.value = followingsSearchResult.value.result.reduce<FollowersMap>(reduceToMapCallback, {});
          } else {
            const buffer = await providers.FollowingsProvider.searchFollowings({
              ids: response.result.map(i => (isFollowingPage ? i.userId : i.followerId)),
            });
            const followersMap = buffer.reduce<FollowersMap>(reduceToMapCallback, {});
            authorFollowings.value = { ...authorFollowings.value, ...followersMap };
          }
        }
      } finally {
        scroll.value = followingsSearchResult.value?.hasNextPage ?? false;
      }
    };

    const getNextPage = (cb: Function) => {
      const nextPage = (followingsSearchResult.value?.page ?? 0) + 1;
      getFollowers(props.isFollowingPage, nextPage, false).finally(() => cb());
    };

    const toggleFollow = useAuthorisedOnly(async (userToToggle: UserPublicData, cb: Function) => {
      const result = await providers.FollowingsProvider.toggle({ id: userToToggle.id });
      cb(result);
    });

    watch(toRef(props, "isFollowingPage"), async (isFollowingPage: boolean) => {
      try {
        followingsSearchResult.value = null;
        loading.value = true;
        authorFollowings.value = {};
        await getFollowers(isFollowingPage);
      } finally {
        loading.value = false;
      }
    });

    const getData = async () => {
      const featureFlag = props.isFollowingPage ? config.featurePublicFollowing : config.featurePublicFollowers;
      if (!featureFlag && !isAuthor.value && !isAdmin.value) {
        root.$router.push("/");
        return;
      }
      try {
        loading.value = true;
        const { result } = await providers.UserPublicDataProvider.search({ data: { id: [props.userId] } });
        user.value = result?.[0] ?? null;

        await getFollowers(props.isFollowingPage);
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      loading,
      scroll,
      isAuthor,
      isAdmin,
      canGetFollowers,
      canGetFollowings,
      followingsSearchResult,
      authorFollowings,
      authUserId,
      user,
      getNextPage,
      toggleFollow,
    };
  },
  beforeRouteEnter(to, from, next) {
    resolveUserIdByRoute(to, from, next);
  },
});
